import { EIntakeStep } from "@rentiohq/shared-frontend/dist/types/application.types";
import { EIntakeType } from "@rentiohq/shared-frontend/dist/types/broker.types";
import { getFlows } from "utils/application.utils";

export const PREFERENCE_TENANT_CONFIRMATION_MAIL_ENABLED =
  "tenant_profile:notification:tenant_confirmation_mail:enabled";
export const PREFERENCE_TENANT_CONFIRMATION_MAIL_NL =
  "tenant_profile:notification:tenant_confirmation_mail:message:nl";
export const PREFERENCE_TENANT_CONFIRMATION_MAIL_FR =
  "tenant_profile:notification:tenant_confirmation_mail:message:fr";
export const PREFERENCE_TENANT_CONFIRMATION_MAIL_EN =
  "tenant_profile:notification:tenant_confirmation_mail:message:en";

// The candidacy confirmation, split off from the visit-request confirmation by
// DEV-3194. Legacy reads these keys when it sends that mail, so the names here
// and there must stay identical; nothing warns you if they drift.
//
// The enabled key has no stored value for any broker yet. Until one is written
// the switch follows the visit-request key above, so a broker who already
// turned confirmations off stays off on both mails (DEV-3206).
export const PREFERENCE_CANDIDACY_CONFIRMATION_MAIL_ENABLED =
  "tenant_profile:notification:candidacy_confirmation_mail:enabled";
export const PREFERENCE_CANDIDACY_CONFIRMATION_MAIL_NL =
  "tenant_profile:notification:candidacy_confirmation_mail:message:nl";
export const PREFERENCE_CANDIDACY_CONFIRMATION_MAIL_FR =
  "tenant_profile:notification:candidacy_confirmation_mail:message:fr";
export const PREFERENCE_CANDIDACY_CONFIRMATION_MAIL_EN =
  "tenant_profile:notification:candidacy_confirmation_mail:message:en";

// The candidacy invitation (DEV-3317). Deliberately without an *_ENABLED key:
// a broker may rewrite this mail but never switch it off, and the absence of
// the key is what guarantees that. Nothing in the backends reads these names;
// the dashboard sends the chosen text on the step call.
export const PREFERENCE_CANDIDATE_TENANT_NL =
  "tenant_profile:notification:candidate_tenant:message:nl";
export const PREFERENCE_CANDIDATE_TENANT_FR =
  "tenant_profile:notification:candidate_tenant:message:fr";
export const PREFERENCE_CANDIDATE_TENANT_EN =
  "tenant_profile:notification:candidate_tenant:message:en";

/**
 * The reminder sent three days after an invitation to a candidate who has
 * not booked a visit (DEV-3329).
 *
 * Absent means on, like the other mails here: the reminder ships on for
 * every office, and an office that wants it off says so.
 *
 * The text is an office's own, one per language, like every other mail on
 * this screen. The button and the "no longer interested" line are not part
 * of it: the sender adds those under whatever the office wrote, so a text
 * that was saved before those existed still gets them.
 */
export const PREFERENCE_BOOKING_REMINDER_ENABLED =
  "tenant_profile:notification:booking_reminder:enabled";
export const PREFERENCE_BOOKING_REMINDER_NL =
  "tenant_profile:notification:booking_reminder:message:nl";
export const PREFERENCE_BOOKING_REMINDER_FR =
  "tenant_profile:notification:booking_reminder:message:fr";
export const PREFERENCE_BOOKING_REMINDER_EN =
  "tenant_profile:notification:booking_reminder:message:en";

export const PREFERENCE_NOT_ELIGIBLE_ENABLED =
  "tenant_profile:notification:not_eligible:enabled";
export const PREFERENCE_NOT_ELIGIBLE_NL = "notEligible_nl_BE";
export const PREFERENCE_NOT_ELIGIBLE_FR = "notEligible_fr_FR";
export const PREFERENCE_NOT_ELIGIBLE_EN = "notEligible_en_UK";

/**
 * The second rejection text, and the office's own name for each of the two
 * (DEV-3325).
 *
 * The first text keeps the keys it has always had, so an office that wrote
 * its own rejection sends exactly that text after this release without
 * touching anything. The second starts out unwritten and falls back to the
 * first, which is why it needs keys of its own rather than a migration.
 *
 * A name is one value, not one per language: only the office reads it, and it
 * is never sent to a candidate.
 */
export const PREFERENCE_NOT_ELIGIBLE_NAME = "notEligible_name";
export const PREFERENCE_NOT_ELIGIBLE_2_NL = "notEligible2_nl_BE";
export const PREFERENCE_NOT_ELIGIBLE_2_FR = "notEligible2_fr_FR";
export const PREFERENCE_NOT_ELIGIBLE_2_EN = "notEligible2_en_UK";
export const PREFERENCE_NOT_ELIGIBLE_2_NAME = "notEligible2_name";

export const PREFERENCE_VISITOR_ENABLED =
  "tenant_profile:notification:visitor:enabled";
export const PREFERENCE_VISITOR_NL =
  "tenant_profile:notification:visitor:message:nl";
export const PREFERENCE_VISITOR_FR =
  "tenant_profile:notification:visitor:message:fr";
export const PREFERENCE_VISITOR_EN =
  "tenant_profile:notification:visitor:message:en";

export const PREFERENCE_PLAN_FOR_VISIT_ENABLED =
  "tenant_profile:notification:plan_for_visit:enabled";
export const PREFERENCE_PLAN_FOR_VISIT_NL =
  "tenant_profile:notification:plan_for_visit:message:nl";
export const PREFERENCE_PLAN_FOR_VISIT_FR =
  "tenant_profile:notification:plan_for_visit:message:fr";
export const PREFERENCE_PLAN_FOR_VISIT_EN =
  "tenant_profile:notification:plan_for_visit:message:en";

export const PREFERENCE_WAITING_LIST_ENABLED =
  "tenant_profile:notification:waiting_list:enabled";
export const PREFERENCE_WAITING_LIST_NL =
  "tenant_profile:notification:waiting_list:message:nl";
export const PREFERENCE_WAITING_LIST_FR =
  "tenant_profile:notification:waiting_list:message:fr";
export const PREFERENCE_WAITING_LIST_EN =
  "tenant_profile:notification:waiting_list:message:en";

/**
 * The mail a candidate gets when the automatic waiting list puts them on it
 * (DEV-3335).
 *
 * No enabled key of its own: the automatic waiting list is switched on per
 * property, so a switch here would be a second, contradicting answer to a
 * question already asked elsewhere.
 */
export const PREFERENCE_AUTO_WAITING_LIST_NL =
  "tenant_profile:notification:auto_waiting_list:message:nl";
export const PREFERENCE_AUTO_WAITING_LIST_FR =
  "tenant_profile:notification:auto_waiting_list:message:fr";
export const PREFERENCE_AUTO_WAITING_LIST_EN =
  "tenant_profile:notification:auto_waiting_list:message:en";

/**
 * The introduction an office puts at the top of the mail that sends
 * candidacies to an owner (DEV-3339).
 *
 * No enabled key: the mail is not a notification that fires on its own, it
 * is sent by a broker from the candidates page, so there is nothing to
 * switch off. This is only the text the window starts from.
 */
export const PREFERENCE_OWNER_SHARE_INTRO_NL =
  "tenant_profile:notification:owner_share_intro:message:nl";
export const PREFERENCE_OWNER_SHARE_INTRO_FR =
  "tenant_profile:notification:owner_share_intro:message:fr";
export const PREFERENCE_OWNER_SHARE_INTRO_EN =
  "tenant_profile:notification:owner_share_intro:message:en";

export const PREFERENCE_INVITED_ENABLED =
  "tenant_profile:notification:invited:enabled";
export const PREFERENCE_INVITED_NL =
  "tenant_profile:notification:invited:message:nl";
export const PREFERENCE_INVITED_FR =
  "tenant_profile:notification:invited:message:fr";
export const PREFERENCE_INVITED_EN =
  "tenant_profile:notification:invited:message:en";

/**
 * The candidacy steps in the order a property's candidates page lays its tabs
 * out, read from the flow that page builds its own tabs from rather than
 * written out again here, so the two cannot drift apart (Jonas, 2026-10-06).
 *
 * The richest flow is used, two phases with a visit, because it is the only
 * one that contains every step an office can have a mail for. Which of those
 * steps a given office actually sees is a question for that office's own
 * flow; this is only about the order they come in.
 */
export const STEP_ORDER: EIntakeStep[] = (() => {
  const flow = getFlows(EIntakeType.TwoPhasesWithVisit, true, true);

  return [
    ...(flow.visitorSteps || []),
    ...flow.candidateSteps,
    ...flow.notEligibleSteps,
  ].map(step => step.id);
})();

/**
 * Where a block sits in that order.
 *
 * `step` is the step whose tab the block belongs to. `within` orders the
 * blocks sharing a step: 0 is the step's own block, and 1, 2, ... are the
 * mails that have no step of their own and follow it. The booking reminder
 * follows the invitation that went unanswered, the automatic waiting list
 * follows the waiting list, and the candidate-tenant step is followed by its
 * confirmation and then the owner intro.
 *
 * A number rather than a flag, because two blocks behind one step would
 * otherwise come out in whatever order the tab list happens to declare them,
 * which is the drift this whole table exists to prevent.
 *
 * A block with no step comes first. The visit-request confirmation is sent
 * the moment a candidacy arrives, before any step has been chosen for it.
 *
 * The candidacy confirmation is not: it goes out when someone becomes a
 * candidate tenant, so it sits behind that step with the owner intro
 * following it, which is the order a broker meets them in (Jonas,
 * 2026-10-06).
 */
export const POSITION_BY_TAB: {
  [tabKey: string]: { step?: EIntakeStep; within?: number };
} = {
  tenant_confirmation_mail: {},
  waiting_list: { step: EIntakeStep.waitingList },
  auto_waiting_list: { step: EIntakeStep.waitingList, within: 1 },
  plan_for_visit: { step: EIntakeStep.planAsVisitor },
  invited: { step: EIntakeStep.invited },
  booking_reminder: { step: EIntakeStep.invited, within: 1 },
  visitor: { step: EIntakeStep.visitor },
  candidate_tenant: { step: EIntakeStep.candidateTenant },
  candidacy_confirmation_mail: { step: EIntakeStep.candidateTenant, within: 1 },
  owner_share_intro: { step: EIntakeStep.candidateTenant, within: 2 },
  not_eligible: { step: EIntakeStep.notEligible },
};

export const getTabRank = (tabKey: string) => {
  const position = POSITION_BY_TAB[tabKey];
  // A block nobody placed sorts last rather than silently first, so a new
  // tab added without a position is visible instead of quietly reordering
  // the screen.
  if (!position) {
    return [STEP_ORDER.length + 1, 0];
  }

  const stepIndex =
    position.step === undefined ? -1 : STEP_ORDER.indexOf(position.step);

  return [stepIndex, position.within || 0];
};
